import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, AlertTriangle, Target, BarChart3 } from 'lucide-react';
import { calculateSavings, getDashboardMetrics } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const adoptionLevels = [
  { rate: 0.2, label: '20%', description: 'Conservative' },
  { rate: 0.4, label: '40%', description: 'Moderate' },
  { rate: 0.6, label: '60%', description: 'Aggressive' },
  { rate: 0.8, label: '80%', description: 'Maximum' }
];

function MetricCard({ icon: Icon, label, value, subvalue, color }) {
  return (
    <div className="metric-card">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <p className="text-sm font-medium text-slate-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-slate-900 tracking-tight">{value}</p>
      {subvalue && <p className="text-sm text-slate-500 mt-1">{subvalue}</p>}
    </div>
  );
}

export default function SimulatorPage() {
  const [adoptionRate, setAdoptionRate] = useState(0.4);
  const [results, setResults] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    async function fetchInitialData() {
      try {
        const [metricsRes, savingsRes] = await Promise.all([
          getDashboardMetrics(),
          calculateSavings(adoptionRate)
        ]);
        setMetrics(metricsRes.data);
        setResults(savingsRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchInitialData();
  }, []);

  useEffect(() => {
    async function fetchAllProjections() {
      try {
        const projections = await Promise.all(
          adoptionLevels.map(level => calculateSavings(level.rate))
        );
        
        const data = adoptionLevels.map((level, idx) => ({
          name: level.label,
          description: level.description,
          low: projections[idx].data.projected_savings_low,
          high: projections[idx].data.projected_savings_high,
          molecules: projections[idx].data.molecules_impacted
        }));
        
        setChartData(data);
      } catch (error) {
        console.error('Failed to fetch projections:', error);
      }
    }
    fetchAllProjections();
  }, []);

  const handleAdoptionChange = async (rate) => {
    setAdoptionRate(rate);
    setLoading(true);
    try {
      const res = await calculateSavings(rate);
      setResults(res.data);
    } catch (error) {
      console.error('Failed to calculate savings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value?.toFixed(0) || 0}`;
  };

  return (
    <div className="p-6" data-testid="simulator-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">Savings Simulator</h1>
        <p className="text-slate-500">Model potential cost savings based on generic adoption scenarios</p>
      </div>

      {/* Adoption Toggle */}
      <div className="card-premium p-6 mb-6" data-testid="adoption-toggle">
        <h2 className="font-semibold text-slate-900 mb-4">Select Adoption Scenario</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {adoptionLevels.map(level => (
            <button
              key={level.rate}
              onClick={() => handleAdoptionChange(level.rate)}
              className={`p-4 rounded-xl border-2 transition-all ${
                adoptionRate === level.rate 
                  ? 'border-[#0A2540] bg-[#0A2540]/5' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`adoption-${level.label}`}
            >
              <p className={`text-2xl font-bold ${adoptionRate === level.rate ? 'text-[#0A2540]' : 'text-slate-900'}`}>
                {level.label}
              </p>
              <p className="text-sm text-slate-500">{level.description}</p>
            </button>
          ))}
        </div>
        <p className="text-sm text-slate-500 mt-4">
          Adoption rate: Percentage of eligible volume moved to validated generic alternatives
        </p>
      </div>

      {/* Results Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <MetricCard
          icon={DollarSign}
          label="Projected Savings (Low)"
          value={formatCurrency(results?.projected_savings_low || 0)}
          color="bg-emerald-500"
        />
        <MetricCard
          icon={TrendingUp}
          label="Projected Savings (High)"
          value={formatCurrency(results?.projected_savings_high || 0)}
          color="bg-emerald-600"
        />
        <MetricCard
          icon={Target}
          label="Molecules Impacted"
          value={results?.molecules_impacted || 0}
          color="bg-[#0A2540]"
        />
        <MetricCard
          icon={AlertTriangle}
          label="Flagged Risks"
          value={results?.flagged_risks || 0}
          subvalue="Require review"
          color="bg-amber-500"
        />
      </div>

      {/* Chart */}
      <div className="card-premium p-6 mb-6" data-testid="savings-chart">
        <h2 className="font-semibold text-slate-900 mb-4">Savings by Adoption Level</h2>
        <div className="h-80">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#64748B' }}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#64748B' }}
                  tickLine={false}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Bar 
                  dataKey="low" 
                  name="Conservative Estimate" 
                  fill="#059669" 
                  radius={[4, 4, 0, 0]}
                  maxBarSize={60}
                />
                <Bar 
                  dataKey="high" 
                  name="Optimistic Estimate" 
                  fill="#10B981" 
                  radius={[4, 4, 0, 0]}
                  maxBarSize={60}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-slate-200 border-t-[#0A2540] rounded-full" />
            </div>
          )}
        </div>
      </div>

      {/* What-If Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-premium p-6 bg-slate-50">
          <h2 className="font-semibold text-slate-900 mb-4">Scenario Analysis</h2>
          <div className="space-y-4">
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">At {Math.round(adoptionRate * 100)}% Adoption</p>
              <p className="text-lg font-semibold text-slate-900">
                {formatCurrency(results?.projected_savings_low || 0)} – {formatCurrency(results?.projected_savings_high || 0)}
              </p>
              <p className="text-sm text-slate-600 mt-2">
                Confidence Range: <span className="font-medium">{results?.confidence_range || 'Moderate'}</span>
              </p>
            </div>
            
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Annual Budget Impact</p>
              <p className="text-lg font-semibold text-emerald-600">
                {((results?.projected_savings_low || 0) / (metrics?.estimated_savings_max || 2000000) * 100).toFixed(1)}% 
                – {((results?.projected_savings_high || 0) / (metrics?.estimated_savings_max || 2000000) * 100).toFixed(1)}% reduction
              </p>
            </div>
          </div>
        </div>

        <div className="card-premium p-6">
          <h2 className="font-semibold text-slate-900 mb-4">Governance Considerations</h2>
          <div className="space-y-3">
            {results?.flagged_risks > 0 && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">{results?.flagged_risks} molecules require additional review</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Some opportunities involve clinical or supply chain considerations that need governance approval.
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <BarChart3 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Phased Implementation Recommended</p>
                <p className="text-sm text-blue-700 mt-1">
                  Start with high-confidence substitutions (≥85% score) before expanding to moderate-confidence options.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Decision Support Only</p>
          <p className="text-sm text-amber-700">
            These projections are estimates based on available data. Actual savings may vary based on contract negotiations, 
            market conditions, and implementation factors. All substitution decisions require pharmacist and clinical governance approval.
          </p>
        </div>
      </div>
    </div>
  );
}
